import { useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { CATALOG } from '@/api/mock/catalog';
import { useAddComponent } from '@/api/queries';
import { configPath } from '@/app/scope';
import { useToast } from '@/app/toasts';
import { Button } from '@/components/Button';
import { useFocusFirstInvalid } from '@/components/focus';
import { Dialog } from '@/components/Dialog';
import { Field, Input, Textarea } from '@/components/fields';
import { Notice } from '@/components/primitives';
import type { Scope } from '@/domain/types';
import { ApproverField, ErrorSummary } from '@/features/proposals/decision';
import { explainError, useApproverName } from '@/features/proposals/decisionState';

import { parseLines } from './helpers';
import { ReasonField } from './shared';

/**
 * Add a component to a scope (TDD §11.2 POST /config/{scope}). The API pre-fills the
 * defaults of §8.4; the dialog asks only for what has no default. The caller mounts it per
 * use, so the fields start blank each time and stay put while the dialog fades out.
 */
export function AddComponentDialog({
  scope,
  open,
  onClose,
  existingIds = [],
}: {
  scope: Scope;
  open: boolean;
  onClose: () => void;
  existingIds?: string[];
}) {
  const [capabilityId, setCapabilityId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [source, setSource] = useState('');
  const [checksText, setChecksText] = useState('');
  const [reason, setReason] = useState('');
  const approver = useApproverName();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, fieldErrors);
  const [message, setMessage] = useState<string | null>(null);
  const mutation = useAddComponent(scope);
  const toast = useToast();
  const navigate = useNavigate();
  const listId = useId();

  const available = CATALOG.filter((entry) => !existingIds.includes(entry.id));

  const fillFromCatalog = (id: string) => {
    setCapabilityId(id);
    const entry = CATALOG.find((e) => e.id === id);
    if (entry) {
      if (!displayName) setDisplayName(entry.display_name);
      if (!source) setSource(entry.source);
      if (!checksText) setChecksText(entry.checks.join('\n'));
    }
  };

  const close = () => {
    if (mutation.isPending) return;
    onClose();
  };

  const submit = async () => {
    setFieldErrors({});
    setMessage(null);
    const local: Record<string, string> = {};
    if (!capabilityId.trim()) local.capability_id = 'Choose a capability id.';
    if (!displayName.trim()) local.display_name = 'Give a display name.';
    if (!source.trim()) local.source = 'Name the runner.';
    if (parseLines(checksText).length === 0) local.checks = 'List at least one check.';
    if (!reason.trim()) local.reason = 'Give a reason.';
    if (!approver.name.trim()) local.changed_by = 'Say who is making the change.';
    if (Object.keys(local).length > 0) {
      setFieldErrors(local);
      return;
    }
    approver.remember();
    try {
      const { config } = await mutation.mutateAsync({
        capability_id: capabilityId.trim(),
        display_name: displayName.trim(),
        source: source.trim(),
        checks: parseLines(checksText),
        changed_by: approver.name.trim(),
        reason: reason.trim(),
      });
      toast.push({
        tone: 'success',
        title: `Added ${config.display_name} with the standard rules`,
      });
      onClose();
      void navigate(configPath(scope, config.capability_id));
    } catch (error) {
      const explained = explainError(error);
      setFieldErrors(explained.fields);
      setMessage(explained.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Add a component"
      description="Created with the standard rules; change them afterwards on the component's page."
      busy={mutation.isPending}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="add-component-form"
            loading={mutation.isPending}
          >
            Add component
          </Button>
        </>
      }
    >
      <form
        ref={formRef}
        id="add-component-form"
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Field
          label="Capability id"
          required
          error={fieldErrors.capability_id}
          hint="Choosing a catalog id fills in the other fields."
        >
          {(props) => (
            <>
              <Input
                {...props}
                name="capability_id"
                list={listId}
                autoComplete="off"
                spellCheck={false}
                value={capabilityId}
                onChange={(e) => fillFromCatalog(e.target.value)}
                placeholder="e.g. import.cede"
                disabled={mutation.isPending}
              />
              <datalist id={listId}>
                {available.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.display_name}
                  </option>
                ))}
              </datalist>
            </>
          )}
        </Field>

        <Field
          label="Display name"
          required
          error={fieldErrors.display_name}
          hint="Customer-facing."
        >
          {(props) => (
            <Input
              {...props}
              name="display_name"
              autoComplete="off"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. CEDE Import"
              disabled={mutation.isPending}
            />
          )}
        </Field>

        <Field
          label="Source"
          required
          error={fieldErrors.source}
          hint="The runner identity allowed to post runs."
        >
          {(props) => (
            <Input
              {...props}
              name="source"
              autoComplete="off"
              spellCheck={false}
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="e.g. gha-synergy-imports"
              disabled={mutation.isPending}
            />
          )}
        </Field>

        <Field label="Checks" required error={fieldErrors.checks} hint="One per line.">
          {(props) => (
            <Textarea
              {...props}
              name="checks"
              rows={4}
              spellCheck={false}
              value={checksText}
              onChange={(e) => setChecksText(e.target.value)}
              placeholder={'get_mapping_sets\ncsv_import_job_completes'}
              disabled={mutation.isPending}
            />
          )}
        </Field>

        <ReasonField
          value={reason}
          onChange={setReason}
          error={fieldErrors.reason}
          disabled={mutation.isPending}
          placeholder="e.g. New capability launched in this region."
        />
        <ApproverField
          label="Changed by"
          value={approver.name}
          onChange={approver.setName}
          error={fieldErrors.changed_by}
          disabled={mutation.isPending}
        />

        {available.length === 0 ? (
          <Notice tone="info">
            Every component in the catalog is already configured for this scope.
          </Notice>
        ) : null}
        <ErrorSummary message={message} />
      </form>
    </Dialog>
  );
}
